/**
 * useAutomation — React hook for automation state
 *
 * Provides tracked forms, plan usage, and mutation helpers for:
 * - Toggling automation per form
 * - Enqueueing manual backups
 * - Syncing provider forms with tracked_forms table
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getTrackedForms,
  getUserOrganization,
  getPlanUsage,
  toggleFormAutomation,
  enqueueManualBackup,
  syncTrackedForms,
  getAutomationEvents,
  type TrackedForm,
  type Organization,
  type PlanUsage,
  type AutomationEvent,
} from '@/services/automationService';
import type { FormProvider } from '@/types/canonical';
import { toast } from '@/components/ui/use-toast';

export function useAutomation() {
  const queryClient = useQueryClient();

  // Organization (with plan tier)
  const orgQuery = useQuery<Organization | null>({
    queryKey: ['organization'],
    queryFn: getUserOrganization,
    staleTime: 5 * 60 * 1000,
  });

  // Tracked forms
  const trackedFormsQuery = useQuery<TrackedForm[]>({
    queryKey: ['trackedForms'],
    queryFn: getTrackedForms,
    staleTime: 30 * 1000,
    enabled: !!orgQuery.data,
  });

  // Plan usage
  const planUsageQuery = useQuery<PlanUsage | null>({
    queryKey: ['planUsage'],
    queryFn: getPlanUsage,
    staleTime: 60 * 1000,
    enabled: !!orgQuery.data,
  });

  // Automation events (last 50)
  const eventsQuery = useQuery<AutomationEvent[]>({
    queryKey: ['automationEvents'],
    queryFn: () => getAutomationEvents(50),
    staleTime: 30 * 1000,
    enabled: !!orgQuery.data,
  });

  // Toggle automation mutation
  const toggleMutation = useMutation({
    mutationFn: ({ trackedFormId, enabled }: { trackedFormId: string; enabled: boolean }) =>
      toggleFormAutomation(trackedFormId, enabled),
    onSuccess: (result, variables) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['trackedForms'] });
        queryClient.invalidateQueries({ queryKey: ['organization'] });
        queryClient.invalidateQueries({ queryKey: ['planUsage'] });
        toast({
          title: variables.enabled ? 'Automation enabled' : 'Automation paused',
          description: variables.enabled
            ? 'This form will be included in the next automated backup cycle.'
            : 'Automated backups paused for this form.',
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Failed to update automation',
          description: result.error,
        });
      }
    },
  });

  // Manual backup mutation
  const manualBackupMutation = useMutation({
    mutationFn: (trackedFormId: string) => enqueueManualBackup(trackedFormId),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['trackedForms'] });
        queryClient.invalidateQueries({ queryKey: ['planUsage'] });
        toast({
          title: 'Backup queued',
          description: 'Your manual backup has been queued and will run shortly.',
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Backup failed',
          description: result.error,
        });
      }
    },
  });

  // Sync forms mutation (called when provider forms list loads)
  const syncFormsMutation = useMutation({
    mutationFn: ({
      organizationId,
      provider,
      forms,
    }: {
      organizationId: string;
      provider: FormProvider;
      forms: Array<{ id: string; name: string; modified?: string; status?: string }>;
    }) => syncTrackedForms(organizationId, provider, forms),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trackedForms'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
  });

  // Helper: find tracked form by external ID + provider
  function getTrackedForm(formIdExternal: string, provider: FormProvider): TrackedForm | undefined {
    return trackedFormsQuery.data?.find(
      (tf) => tf.form_id_external === String(formIdExternal) && tf.provider === provider
    );
  }

  return {
    // Queries
    organization: orgQuery.data,
    trackedForms: trackedFormsQuery.data || [],
    planUsage: planUsageQuery.data,
    events: eventsQuery.data || [],
    isLoading: orgQuery.isLoading || trackedFormsQuery.isLoading,

    // Mutations
    toggleAutomation: toggleMutation.mutate,
    isToggling: toggleMutation.isPending,
    enqueueManualBackup: manualBackupMutation.mutate,
    isEnqueueing: manualBackupMutation.isPending,
    syncForms: syncFormsMutation.mutate,

    // Helpers
    getTrackedForm,
  };
}
